///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Fragment, Component } from "react";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import { Tab, TabList } from "react-tabs";

import LanesGrid from "./pricing/LanesGrid";
import { fetchBidSummary } from "../../slices/bidSummarySlice";
import NoPricingLanes from "./pricing/NoPricingLanes";
import { userRole } from "../../helpers/types";
import LaneMatch from "./lane-match/LaneMatch";
import messages from "../../helpers/messages";
import tableSelection, {
  clearSelection,
  clearCustomSelection,
  selectedCellClassName
} from "../../helpers/tableSelection";
import { resetLaneMatchState } from "../../slices/laneMatchSlice";
import NavigationBlocker from "../NavigationBlocker";
import Analytics from "./analytics/Analytics";
import {
  reset as resetAnalytics,
  setFullScreenMode
} from "../../slices/analyticsSlice";
import AnalyticsGridSection from "./analytics/AnalyticsGridSection";

export class Pricing extends Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.loadingRef = React.createRef();
    this.laneMatchRef = React.createRef();
    this.analyticsRef = React.createRef();
  }

  beforeunload = event => {
    const isChanged = lanes =>
      lanes && lanes.length && lanes.some(l => l.metaData.laneChanged);
    const { laneMatch, bidPricing } = this.props;

    if (isChanged(laneMatch.lanes) || isChanged(bidPricing.lanes)) {
      event.preventDefault();
      event.returnValue = "";
    }
  };

  listener = event => {
    if (this.ref.current && !this.ref.current.contains(event.target)) {
      clearSelection();
      clearCustomSelection();
      window.startCell = window.endCell = undefined;
    }
  };

  clearIfCustomSelection = () => {
    if (document.getElementsByClassName(selectedCellClassName).length) {
      clearCustomSelection();
      clearSelection();
    }
  };

  componentDidMount() {
    const {
      dispatch,
      match: {
        params: { bidId }
      },
      user: {
        user: { division }
      },
      role
    } = this.props;

    dispatch(
      fetchBidSummary({
        bidId,
        role,
        fetchTLLaneCount: false,
        division,
        fetchLanes: true
      })
    );
    dispatch(resetLaneMatchState());
    dispatch(resetAnalytics());

    document.addEventListener("mousedown", this.listener);
    window.addEventListener("blur", this.clearIfCustomSelection);
    window.addEventListener("beforeunload", this.beforeunload);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.role !== this.props.role) {
      const {
        dispatch,
        match: {
          params: { bidId }
        },
        user: {
          user: { division }
        },
        role
      } = this.props;

      dispatch(
        fetchBidSummary({
          bidId,
          role,
          fetchTLLaneCount: false,
          division,
          fetchLanes: true
        })
      );
      dispatch(resetLaneMatchState());
      dispatch(resetAnalytics());
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.listener);
    window.removeEventListener("blur", this.clearIfCustomSelection);
    window.removeEventListener("beforeunload", this.beforeunload);
  }

  toggleTabs({ loading = false, laneMatch = false, analytics = false }) {
    const toggleTab = ({ ref, show }) => {
      if (show) {
        ref?.current?.classList?.remove("hidden");
      } else if (!ref?.current?.classList?.contains("hidden")) {
        ref?.current?.classList?.add("hidden");
      }
    };

    toggleTab({ ref: this.loadingRef, show: loading });
    toggleTab({ ref: this.laneMatchRef, show: laneMatch });
    toggleTab({ ref: this.analyticsRef, show: analytics });
  }

  handleTabClick = route => {
    this.toggleTabs({ loading: true });

    setTimeout(() => {
      const { history, dispatch } = this.props;
      history.push("./" + route);
      dispatch(setFullScreenMode(false));
    });
  };

  render() {
    const {
      match,
      bidPricing: {
        lanes: pricingLanes,
        isLaneImportInProgress,
        lanesError: pricingError,
        pricingProgress: { isLaneRepriceInProgress },
        useRxoTlServerPagedLanes,
        totalFiltered,
        filtersApplied
      },
      laneMatch: { lanes: matchingLanes },
      user: {
        user: { division }
      },
      history,
      analytics: { isFullScreenMode },
      role
    } = this.props;
    const bidId = match.params.bidId;
    const unsavedChanges = lanes =>
      lanes && lanes.length && lanes.some(l => l.metaData.laneChanged === true)
        ? true
        : false;

    const path = match.path;
    const route = history.location.pathname.split("/").splice(-1)[0];

    const LANE_MATCH = "lane-match";
    const ANALYTICS = "analytics";

    const isEmpty = pricingLanes != null && pricingLanes.length === 0;
    const showNoPricingLanes =
      isEmpty &&
      !(useRxoTlServerPagedLanes && (totalFiltered > 0 || filtersApplied));

    return (
      <div
        className="bid pricing"
        ref={this.ref}
        onCopy={tableSelection.handleCopy}
      >
        <NavigationBlocker
          when={unsavedChanges(matchingLanes) || unsavedChanges(pricingLanes)}
        />

        {showNoPricingLanes ? (
          <NoPricingLanes
            role={role}
            bidId={bidId}
            division={division}
            isLaneImportInProgress={isLaneImportInProgress}
            isLaneRepriceInProgress={isLaneRepriceInProgress}
          />
        ) : (
          <Fragment>
            {(role === userRole.salesRep ||
              role === userRole.tlPricingAnalyst ||
              role === userRole.assetBasedPricingUser) &&
              !pricingError && (
                <Fragment>
                  <TabList>
                    <Tab
                      selected={route === LANE_MATCH}
                      onClick={() => this.handleTabClick(LANE_MATCH)}
                    >
                      {messages.laneMatch.defaultMessage}
                    </Tab>
                    <Tab
                      selected={route === ANALYTICS}
                      onClick={() => this.handleTabClick(ANALYTICS)}
                    >
                      {messages.analytics.defaultMessage}
                    </Tab>
                  </TabList>
                  <div
                    ref={this.loadingRef}
                    className="box panel tab-loader text-light-grey weight--semibold"
                  >
                    {messages.loading.defaultMessage}
                  </div>
                  <Switch>
                    <Route
                      exact={false}
                      path={path + "/" + LANE_MATCH}
                      render={() => (
                        <div
                          ref={this.laneMatchRef}
                          className="lane-match-container"
                        >
                          <LaneMatch
                            bidId={bidId}
                            role={role}
                            division={division}
                            onMount={() => this.toggleTabs({ laneMatch: true })}
                          />
                        </div>
                      )}
                    />
                    <Route
                      path={path + "/" + ANALYTICS}
                      render={() => (
                        <div
                          ref={this.analyticsRef}
                          className="analytics-container"
                        >
                          <Analytics
                            userDivision={division}
                            onMount={() => this.toggleTabs({ analytics: true })}
                          />
                        </div>
                      )}
                    />
                    <Redirect to={path + "/" + LANE_MATCH} />
                  </Switch>
                </Fragment>
              )}

            {!isFullScreenMode ? (
              <LanesGrid bidId={bidId} role={role} division={division} />
            ) : (
              <AnalyticsGridSection />
            )}
          </Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  bidPricing: state.bidPricing,
  user: state.user,
  laneMatch: state.laneMatch,
  featureFlags: state.featureFlags,
  analytics: state.analytics
});

export default connect(mapStateToProps)(Pricing);
